<template>
  <q-dialog
    :model-value="modelValue"
    persistent
    maximized
    transition-show="slide-up"
    transition-hide="slide-down"
    class="tender-form-dialog"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <q-card class="tender-form">
      <!-- Enhanced Header -->
      <q-card-section class="form-header bg-primary">
        <div class="row items-center">
          <div class="col">
            <div class="row items-center">
              <q-avatar
                color="white"
                text-color="primary"
                size="40px"
                class="q-mr-md"
              >
                <q-icon :name="isEdit ? 'edit' : 'add'" size="24px" />
              </q-avatar>
              <div>
                <div class="text-h5 text-weight-bold text-white">
                  {{ isEdit ? 'Edit Tender' : 'Create New Tender' }}
                </div>
                <div class="text-body2 text-blue-2">
                  {{ isEdit ? 'Update tender information and settings' : 'Set up a new tender for your project' }}
                </div>
              </div>
            </div>
          </div>
          <div class="col-auto">
            <q-btn
              flat
              round
              icon="close"
              size="md"
              text-color="white"
              @click="onCancel"
            >
              <q-tooltip>Close</q-tooltip>
            </q-btn>
          </div>
        </div>
      </q-card-section>

      <!-- Form Content -->
      <q-card-section class="form-content">
        <q-form ref="tenderForm" class="q-gutter-lg" @submit="onSubmit">
          <!-- Basic Information Section -->
          <div class="form-section">
            <div class="section-header">
              <q-icon name="info" color="primary" size="24px" class="q-mr-sm" />
              <div class="section-title">Basic Information</div>
            </div>
            
            <div class="section-content">
              <div class="row q-col-gutter-md">
                <!-- Tender Name -->
                <div class="col-12 col-md-8">
                  <q-input
                    v-model="form.name"
                    label="Tender Name"
                    outlined
                    :rules="[val => !!val || 'Tender name is required']"
                    hint="Enter a descriptive name for this tender"
                    class="enhanced-input"
                  >
                    <template #prepend>
                      <q-icon name="title" />
                    </template>
                  </q-input>
                </div>

                <!-- Reference Number -->
                <div class="col-12 col-md-4">
                  <q-input
                    v-model="form.reference_number"
                    label="Reference Number"
                    outlined
                    hint="External reference or job number"
                    class="enhanced-input"
                  >
                    <template #prepend>
                      <q-icon name="tag" />
                    </template>
                  </q-input>
                </div>

                <!-- Description -->
                <div class="col-12">
                  <q-input
                    v-model="form.description"
                    label="Description"
                    outlined
                    type="textarea"
                    autogrow
                    rows="3"
                    hint="Provide a detailed description of the tender requirements"
                    class="enhanced-input"
                  >
                    <template #prepend>
                      <q-icon name="description" />
                    </template>
                  </q-input>
                </div>
              </div>
            </div>
          </div>

          <!-- Project Details Section -->
          <div class="form-section">
            <div class="section-header">
              <q-icon name="business" color="secondary" size="24px" class="q-mr-sm" />
              <div class="section-title">Project Details</div>
            </div>
            
            <div class="section-content">
              <div class="row q-col-gutter-md">
                <!-- Site Selection -->
                <div class="col-12 col-md-6">
                  <SiteAutocomplete
                    v-model="form.site_id"
                    label="Site *"
                    required
                  />
                </div>

                <!-- Status -->
                <div class="col-12 col-md-6">
                  <q-select
                    v-model="form.status"
                    :options="statusOptions"
                    label="Status"
                    outlined
                    emit-value
                    map-options
                    class="enhanced-select"
                  >
                    <template #prepend>
                      <q-icon name="flag" />
                    </template>
                  </q-select>
                </div>

                <!-- Project Value Estimate -->
                <div class="col-12 col-md-6">
                  <q-input
                    v-model="form.estimated_value"
                    label="Estimated Project Value"
                    outlined
                    type="number"
                    prefix="$"
                    hint="Rough estimate of the project value"
                    class="enhanced-input"
                  >
                    <template #prepend>
                      <q-icon name="attach_money" />
                    </template>
                  </q-input>
                </div>

                <!-- Project Type -->
                <div class="col-12 col-md-6">
                  <q-select
                    v-model="form.project_type"
                    :options="projectTypeOptions"
                    label="Project Type"
                    outlined
                    emit-value
                    map-options
                    class="enhanced-select"
                  >
                    <template #prepend>
                      <q-icon name="category" />
                    </template>
                  </q-select>
                </div>
              </div>
            </div>
          </div>

          <!-- Timeline Section -->
          <div class="form-section">
            <div class="section-header">
              <q-icon name="schedule" color="accent" size="24px" class="q-mr-sm" />
              <div class="section-title">Timeline & Dates</div>
            </div>

            <div class="section-content">
              <div class="row q-col-gutter-md">
                <!-- Due Date -->
                <div class="col-12 col-md-4">
                  <q-input
                    v-model="form.due_date"
                    label="Due Date"
                    outlined
                    type="date"
                    hint="When is this tender due?"
                    class="enhanced-input"
                  >
                    <template #prepend>
                      <q-icon name="event" />
                    </template>
                  </q-input>
                </div>

                <!-- Submission Date -->
                <div class="col-12 col-md-4">
                  <q-input
                    v-model="form.submission_date"
                    label="Submission Date"
                    outlined
                    type="date"
                    :disable="form.status !== 'submitted'"
                    hint="When was this tender submitted?"
                    class="enhanced-input"
                  >
                    <template #prepend>
                      <q-icon name="send" />
                    </template>
                  </q-input>
                </div>

                <!-- Expected Start Date -->
                <div class="col-12 col-md-4">
                  <q-input
                    v-model="form.expected_start_date"
                    label="Expected Start Date"
                    outlined
                    type="date"
                    hint="When is the project expected to start?"
                    class="enhanced-input"
                  >
                    <template #prepend>
                      <q-icon name="play_arrow" />
                    </template>
                  </q-input>
                </div>

                <!-- Requested Date (New) -->
                <div class="col-12 col-md-4">
                  <q-input
                    v-model="form.requested_date"
                    label="Requested Date"
                    outlined
                    type="date"
                    hint="When was this tender received?"
                    class="enhanced-input"
                  >
                    <template #prepend>
                      <q-icon name="inbox" />
                    </template>
                  </q-input>
                </div>

                <!-- Expected Order Date -->
                <div class="col-12 col-md-4">
                  <q-select
                    v-model="form.expected_order_date"
                    :options="expectedOrderDateOptions"
                    label="Expected Order Date"
                    outlined
                    dense
                    emit-value
                    map-options
                    clearable
                    hint="When do we expect the PO?"
                    class="enhanced-select"
                  >
                    <template #prepend>
                      <q-icon name="receipt_long" />
                    </template>
                  </q-select>
                </div>

                <!-- Indicative Program (New) -->
                <div class="col-12 col-md-4">
                  <q-input
                    v-model="form.indicative_program"
                    label="Indicative Program"
                    outlined
                    hint="E.g., 'Feb 2026 Start', '16 months'"
                    class="enhanced-input"
                  >
                    <template #prepend>
                      <q-icon name="timeline" />
                    </template>
                  </q-input>
                </div>
              </div>
            </div>
          </div>

          <!-- Pipeline Information Section (New) -->
          <div class="form-section">
            <div class="section-header">
              <q-icon name="trending_up" color="teal" size="24px" class="q-mr-sm" />
              <div class="section-title">Pipeline Information</div>
            </div>

            <div class="section-content">
              <div class="row q-col-gutter-md">
                <!-- Market Sector -->
                <div class="col-12 col-md-6">
                  <q-select
                    v-model="form.market_sector_id"
                    :options="filteredMarketSectorOptions"
                    label="Market Sector"
                    outlined
                    emit-value
                    map-options
                    clearable
                    use-input
                    input-debounce="0"
                    :loading="loadingMarketSectors"
                    class="enhanced-select"
                    new-value-mode="add-unique"
                    hint="Type to search or add a new sector"
                    @new-value="createMarketSector"
                    @filter="filterMarketSectors"
                  >
                    <template #prepend>
                      <q-icon name="category" />
                    </template>
                    <template #option="scope">
                      <q-item v-bind="scope.itemProps">
                        <q-item-section avatar>
                          <q-icon :name="scope.opt.icon || 'business'" :color="scope.opt.color || 'primary'" />
                        </q-item-section>
                        <q-item-section>
                          <q-item-label>{{ scope.opt.label }}</q-item-label>
                        </q-item-section>
                      </q-item>
                    </template>
                    <template #no-option>
                      <q-item>
                        <q-item-section class="text-grey">
                          Type to add a new sector
                        </q-item-section>
                      </q-item>
                    </template>
                  </q-select>
                </div>

                <!-- Tender Type -->
                <div class="col-12 col-md-6">
                  <q-select
                    v-model="form.tender_type"
                    :options="tenderTypeOptions"
                    label="Tender Type"
                    outlined
                    emit-value
                    map-options
                    clearable
                    class="enhanced-select"
                  >
                    <template #prepend>
                      <q-icon name="assignment" />
                    </template>
                  </q-select>
                </div>

                <!-- Construction Value -->
                <div class="col-12 col-md-6">
                  <q-input
                    v-model.number="form.construction_value"
                    label="Construction Value"
                    outlined
                    type="number"
                    prefix="$"
                    hint="Total construction value of the project"
                    class="enhanced-input"
                  >
                    <template #prepend>
                      <q-icon name="account_balance" />
                    </template>
                  </q-input>
                </div>

                <!-- Default Markup -->
                <div class="col-12 col-md-6">
                  <q-input
                    v-model.number="form.default_markup"
                    label="Default Markup %"
                    outlined
                    type="number"
                    min="0"
                    max="200"
                    step="0.5"
                    hint="Default markup for quotes in this tender"
                    class="enhanced-input"
                  >
                    <template #prepend>
                      <q-icon name="percent" />
                    </template>
                  </q-input>
                </div>
              </div>
            </div>
          </div>

          <!-- Organizations Section (New) -->
          <div class="form-section">
            <div class="section-header">
              <q-icon name="business" color="purple" size="24px" class="q-mr-sm" />
              <div class="section-title">Organizations</div>
            </div>

            <div class="section-content">
              <div class="row q-col-gutter-md">
                <!-- Consultant -->
                <div class="col-12 col-md-6">
                  <OrganizationAutocomplete
                    v-model="form.consultant_id"
                    type="consultant"
                    label="Consultant"
                    @selected="onConsultantSelected"
                  />
                </div>

                <!-- Builder -->
                <div class="col-12 col-md-6">
                  <OrganizationAutocomplete
                    v-model="form.builder_id"
                    type="builder"
                    label="Builder"
                    @selected="onBuilderSelected"
                  />
                </div>

                <!-- Builder Status -->
                <div class="col-12 col-md-6">
                  <q-select
                    v-model="form.builder_status"
                    :options="builderStatusOptions"
                    label="Builder Status"
                    outlined
                    emit-value
                    map-options
                    clearable
                    :disable="!form.builder_id"
                    class="enhanced-select"
                  >
                    <template #prepend>
                      <q-icon name="how_to_reg" />
                    </template>
                  </q-select>
                </div>
              </div>
            </div>
          </div>

          <!-- Technical Flags Section -->
          <div class="form-section">
            <div class="section-header">
              <q-icon name="engineering" color="cyan" size="24px" class="q-mr-sm" />
              <div class="section-title">Technical Flags</div>
            </div>

            <div class="section-content">
              <div class="row q-col-gutter-md">
                <div class="col-12 col-md-4">
                  <q-select
                    v-model="form.bms_specified"
                    :options="bmsSpecifiedOptions"
                    label="BMS Specified"
                    outlined
                    dense
                    emit-value
                    map-options
                    clearable
                    class="enhanced-select"
                  />
                </div>
                <div class="col-12 col-md-4">
                  <q-select
                    v-model="form.tridium_specified"
                    :options="yesNoOptions"
                    label="Tridium Specified"
                    outlined
                    dense
                    emit-value
                    map-options
                    clearable
                    class="enhanced-select"
                  />
                </div>
                <div class="col-12 col-md-4">
                  <q-select
                    v-model="form.bpic_site"
                    :options="yesNoOptions"
                    label="BPIC Site"
                    outlined
                    dense
                    emit-value
                    map-options
                    clearable
                    class="enhanced-select"
                  />
                </div>
                <div class="col-12 col-md-4">
                  <q-select
                    v-model="form.eba_site"
                    :options="yesNoOptions"
                    label="EBA Site"
                    outlined
                    dense
                    emit-value
                    map-options
                    clearable
                    class="enhanced-select"
                  />
                </div>
                <div class="col-12 col-md-4">
                  <q-select
                    v-model="form.includes_install"
                    :options="includesInstallOptions"
                    label="Includes Install"
                    outlined
                    dense
                    emit-value
                    map-options
                    clearable
                    class="enhanced-select"
                  />
                </div>
                <div class="col-12 col-md-4">
                  <q-select
                    v-model="form.mech_elec"
                    :options="mechElecOptions"
                    label="Mech/Elec"
                    outlined
                    dense
                    emit-value
                    map-options
                    clearable
                    class="enhanced-select"
                  />
                </div>
                <div class="col-12 col-md-4">
                  <q-select
                    v-model="form.mech_elec_status"
                    :options="mechElecStatusOptions"
                    label="Mech/Elec Status"
                    outlined
                    dense
                    emit-value
                    map-options
                    clearable
                    class="enhanced-select"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Competitor Information -->
          <div class="form-section">
            <div class="section-header">
              <q-icon name="leaderboard" color="orange" size="24px" class="q-mr-sm" />
              <div class="section-title">Competitor Information</div>
            </div>

            <div class="section-content">
              <div class="row q-col-gutter-md">
                <div class="col-12 col-md-4">
                  <q-select
                    v-model="form.competitor"
                    :options="filteredCompetitorOptions"
                    label="Competitor"
                    outlined
                    dense
                    emit-value
                    map-options
                    clearable
                    use-input
                    input-debounce="0"
                    new-value-mode="add-unique"
                    class="enhanced-select"
                    @filter="filterCompetitors"
                  />
                </div>
                <div class="col-12 col-md-4">
                  <q-select
                    v-model="form.competitor_status"
                    :options="competitorStatusOptions"
                    label="Competitor Status"
                    outlined
                    dense
                    emit-value
                    map-options
                    clearable
                    class="enhanced-select"
                  />
                </div>
                <div class="col-12 col-md-4">
                  <q-input
                    v-model="form.competitor_price"
                    label="Competitor Price"
                    outlined
                    dense
                    type="number"
                    prefix="$"
                    class="enhanced-input"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Assignment Section (New) -->
          <div class="form-section">
            <div class="section-header">
              <q-icon name="assignment_ind" color="indigo" size="24px" class="q-mr-sm" />
              <div class="section-title">Assignment</div>
            </div>

            <div class="section-content">
              <div class="row q-col-gutter-md">
                <!-- Estimator -->
                <div class="col-12 col-md-4">
                  <q-select
                    v-model="form.estimator_id"
                    :options="userOptions"
                    label="Estimator"
                    outlined
                    emit-value
                    map-options
                    clearable
                    :loading="loadingUsers"
                    class="enhanced-select"
                  >
                    <template #prepend>
                      <q-icon name="person" />
                    </template>
                  </q-select>
                </div>

                <!-- Estimator 2 -->
                <div class="col-12 col-md-4">
                  <q-select
                    v-model="form.estimator_2_id"
                    :options="userOptions"
                    label="Estimator 2"
                    outlined
                    emit-value
                    map-options
                    clearable
                    :loading="loadingUsers"
                    class="enhanced-select"
                  >
                    <template #prepend>
                      <q-icon name="person_add" />
                    </template>
                  </q-select>
                </div>

                <!-- Project Manager -->
                <div class="col-12 col-md-4">
                  <q-select
                    v-model="form.project_manager_id"
                    :options="userOptions"
                    label="Project Manager"
                    outlined
                    emit-value
                    map-options
                    clearable
                    :loading="loadingUsers"
                    class="enhanced-select"
                  >
                    <template #prepend>
                      <q-icon name="manage_accounts" />
                    </template>
                  </q-select>
                </div>
              </div>
            </div>
          </div>

          <!-- Contractors Section (only for new tenders) -->
          <div v-if="!isEdit" class="form-section">
            <div class="section-header">
              <q-icon name="people" color="orange" size="24px" class="q-mr-sm" />
              <div class="section-title">Contractors</div>
              <div class="section-subtitle">Add contractors for this tender</div>
            </div>

            <div class="section-content">
              <!-- List of added contractors -->
              <div v-for="(contractor, index) in form.contractors" :key="index" class="contractor-row q-mb-md">
                <q-card flat bordered class="q-pa-md">
                  <div class="row q-col-gutter-md items-start">
                    <!-- Organization -->
                    <div class="col-12 col-md-4">
                      <OrganizationAutocomplete
                        v-model="contractor.organization_id"
                        type="contractor"
                        label="Contractor *"
                        required
                        @selected="(org) => onContractorOrgSelected(index, org)"
                      />
                    </div>
                    <!-- Contact -->
                    <div class="col-12 col-md-3">
                      <q-select
                        v-model="contractor.organization_contact_id"
                        :options="contractor._contactOptions || []"
                        label="Contact"
                        outlined
                        emit-value
                        map-options
                        clearable
                        :disable="!contractor.organization_id"
                        hint="Primary contact"
                      >
                        <template #prepend>
                          <q-icon name="person" />
                        </template>
                      </q-select>
                    </div>
                    <!-- Status -->
                    <div class="col-12 col-md-2">
                      <q-select
                        v-model="contractor.status"
                        :options="contractorStatusOptions"
                        label="Status"
                        outlined
                        emit-value
                        map-options
                      />
                    </div>
                    <!-- Primary -->
                    <div class="col-6 col-md-2">
                      <q-checkbox
                        v-model="contractor.is_primary"
                        label="Primary"
                        @update:model-value="(val) => onPrimaryChange(index, val)"
                      />
                    </div>
                    <!-- Remove -->
                    <div class="col-6 col-md-1 flex items-center justify-end">
                      <q-btn
                        flat
                        round
                        icon="delete"
                        color="negative"
                        size="sm"
                        @click="removeContractor(index)"
                      >
                        <q-tooltip>Remove Contractor</q-tooltip>
                      </q-btn>
                    </div>
                  </div>
                </q-card>
              </div>

              <!-- Add Contractor Button -->
              <q-btn
                outline
                color="primary"
                icon="add"
                label="Add Contractor"
                class="full-width"
                @click="addContractor"
              />
            </div>
          </div>

          <!-- Advanced Options Section -->
          <q-expansion-item
            icon="settings"
            label="Advanced Options"
            header-class="text-primary"
            class="advanced-options"
          >
            <div class="section-content q-pa-md">
              <div class="row q-col-gutter-md">
                <!-- Win Likelihood -->
                <div class="col-12 col-md-6">
                  <div class="likelihood-input">
                    <q-input
                      v-model="form.win_likelihood"
                      label="Win Likelihood (%)"
                      outlined
                      type="number"
                      min="0"
                      max="100"
                      hint="Estimated probability of winning this tender"
                      class="enhanced-input"
                    >
                      <template #prepend>
                        <q-icon name="trending_up" />
                      </template>
                      <template #append>
                        <q-icon name="percent" />
                      </template>
                    </q-input>
                    
                    <div class="likelihood-preview q-mt-sm">
                      <q-linear-progress
                        :value="(form.win_likelihood || 0) / 100"
                        :color="getLikelihoodColor(form.win_likelihood || 0)"
                        size="8px"
                        rounded
                      />
                    </div>
                  </div>
                </div>

                <!-- Internal Priority -->
                <div class="col-12 col-md-6">
                  <q-select
                    v-model="form.priority"
                    :options="priorityOptions"
                    label="Internal Priority"
                    outlined
                    emit-value
                    map-options
                    class="enhanced-select"
                  >
                    <template #prepend>
                      <q-icon name="priority_high" />
                    </template>
                  </q-select>
                </div>

                <!-- Tags -->
                <div class="col-12">
                  <q-select
                    v-model="form.tags"
                    :options="tagOptions"
                    label="Tags"
                    outlined
                    multiple
                    use-chips
                    use-input
                    new-value-mode="add-unique"
                    hint="Add tags to categorize this tender"
                    class="enhanced-select"
                  >
                    <template #prepend>
                      <q-icon name="local_offer" />
                    </template>
                  </q-select>
                </div>

                <!-- Internal Notes -->
                <div class="col-12">
                  <q-input
                    v-model="form.internal_notes"
                    label="Internal Notes"
                    outlined
                    type="textarea"
                    autogrow
                    rows="2"
                    hint="Private notes for internal use only"
                    class="enhanced-input"
                  >
                    <template #prepend>
                      <q-icon name="note" />
                    </template>
                  </q-input>
                </div>
              </div>
            </div>
          </q-expansion-item>
        </q-form>
      </q-card-section>

      <!-- Enhanced Actions Footer -->
      <q-card-actions class="form-actions bg-grey-1">
        <div class="row full-width items-center">
          <div class="col">
            <q-btn
              flat
              icon="help"
              label="Help"
              color="grey-7"
              @click="showHelp"
            />
          </div>
          <div class="col-auto">
            <q-btn-group>
              <q-btn
                flat
                label="Cancel"
                color="grey-7"
                class="action-btn"
                @click="onCancel"
              />
              <q-btn
                v-if="!isEdit"
                label="Save as Draft"
                color="grey-6"
                :loading="savingDraft"
                class="action-btn"
                @click="saveDraft"
              />
              <q-btn
                :label="isEdit ? 'Update Tender' : 'Create Tender'"
                color="primary"
                :loading="tenderStore.isLoading"
                class="action-btn action-btn--primary"
                @click="onSubmit"
              />
            </q-btn-group>
          </div>
        </div>
      </q-card-actions>
    </q-card>

    <!-- Create Site Dialog -->
    <q-dialog v-model="showSiteDialog" persistent>
      <q-card style="min-width: 400px">
        <q-card-section>
          <div class="text-h6">Quick Site Creation</div>
        </q-card-section>
        <q-card-section>
          <q-input
            v-model="newSiteName"
            label="Site Name"
            outlined
            autofocus
          />
        </q-card-section>
        <q-card-actions align="right">
          <q-btn v-close-popup flat label="Cancel" />
          <q-btn label="Create" color="primary" @click="createSite" />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Help Dialog -->
    <q-dialog v-model="showHelpDialog">
      <q-card style="width: 600px; max-width: 80vw">
        <q-card-section>
          <div class="text-h6">Tender Form Help</div>
        </q-card-section>
        <q-card-section>
          <q-list>
            <q-item>
              <q-item-section avatar>
                <q-icon name="info" color="primary" />
              </q-item-section>
              <q-item-section>
                <q-item-label>Tender Name</q-item-label>
                <q-item-label caption>
                  Use a descriptive name that clearly identifies the project or tender purpose.
                </q-item-label>
              </q-item-section>
            </q-item>
            <q-item>
              <q-item-section avatar>
                <q-icon name="business" color="secondary" />
              </q-item-section>
              <q-item-section>
                <q-item-label>Site Selection</q-item-label>
                <q-item-label caption>
                  Choose the site where the work will be performed. This helps organize tenders by location.
                </q-item-label>
              </q-item-section>
            </q-item>
            <q-item>
              <q-item-section avatar>
                <q-icon name="people" color="orange" />
              </q-item-section>
              <q-item-section>
                <q-item-label>Contractors</q-item-label>
                <q-item-label caption>
                  Add contractors individually with contact, status, and primary designation. You can add more contractors later.
                </q-item-label>
              </q-item-section>
            </q-item>
          </q-list>
        </q-card-section>
        <q-card-actions align="right">
          <q-btn v-close-popup flat label="Close" />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </q-dialog>
</template>

<script setup>
  import { ref, computed, onMounted, watch } from 'vue';
  import { useTenderStore } from '../../stores/tenders';
  // Sites store no longer needed - using SiteAutocomplete with search-on-type
  import { useQuasar } from 'quasar';
  import OrganizationAutocomplete from '../OrganizationAutocomplete.vue';
  import SiteAutocomplete from '../SiteAutocomplete.vue';
  import api from '../../services/api';
  import { organizationService } from '../../services/organizationService';

  const props = defineProps({
    modelValue: {
      type: Boolean,
      required: true,
    },
    tender: {
      type: Object,
      default: null,
    },
  });

  const emit = defineEmits(['update:modelValue', 'save', 'cancel']);

  const tenderStore = useTenderStore();
  const $q = useQuasar();

  // Refs
  const tenderForm = ref(null);
  const showSiteDialog = ref(false);
  const showHelpDialog = ref(false);
  const newSiteName = ref('');
  const savingDraft = ref(false);

  // New data loaders
  const loadingMarketSectors = ref(false);
  const loadingUsers = ref(false);
  const marketSectors = ref([]);
  const filteredMarketSectorOptions = ref([]);
  const users = ref([]);

  // Form state
  const form = ref({
    name: '',
    reference_number: '',
    description: '',
    site_id: null,
    status: 'pending',
    due_date: '',
    submission_date: '',
    expected_start_date: '',
    estimated_value: null,
    project_type: '',
    contractors: [],
    win_likelihood: 50,
    priority: 'medium',
    tags: [],
    internal_notes: '',
    // New pipeline fields
    requested_date: '',
    expected_order_date: '',
    indicative_program: '',
    market_sector_id: null,
    tender_type: null,
    construction_value: null,
    default_markup: null,
    consultant_id: null,
    builder_id: null,
    builder_status: null,
    bms_specified: null,
    tridium_specified: false,
    bpic_site: false,
    eba_site: false,
    includes_install: null,
    mech_elec: null,
    mech_elec_status: null,
    competitor: null,
    competitor_status: null,
    competitor_price: null,
    estimator_id: null,
    estimator_2_id: null,
    project_manager_id: null,
  });

  // Options
  const statusOptions = [
    { label: 'Pending', value: 'pending' },
    { label: 'Won', value: 'won' },
    { label: 'Lost', value: 'lost' },
    { label: 'DNQ', value: 'dnq' },
    { label: 'Unknown', value: 'unknown' },
    { label: 'Withdrew', value: 'withdrew' },
    { label: 'Dead', value: 'dead' },
  ];

  const projectTypeOptions = [
    { label: 'New build', value: 'new_build' },
    { label: 'Extension to existing', value: 'extension' },
    { label: 'Controls Retrofit', value: 'controls_retrofit' },
    { label: 'Fitout/Refurb', value: 'fitout_refurb' },
    { label: 'Upgrade', value: 'upgrade' },
  ];

  const priorityOptions = [
    { label: 'Low', value: 'low' },
    { label: 'Medium', value: 'medium' },
    { label: 'High', value: 'high' },
    { label: 'Critical', value: 'critical' },
  ];

  const tagOptions = [
    'HVAC', 'Electrical', 'Plumbing', 'Fire Safety', 'BMS', 'Chiller', 'AHU', 'VAV',
    'Emergency', 'Scheduled', 'Upgrade', 'Retrofit', 'New Build',
  ];

  // New pipeline options - must match backend enums
  const tenderTypeOptions = [
    { label: 'Plan Spec', value: 'plan_spec' },
    { label: 'D&C', value: 'd_and_c' },
    { label: 'End User', value: 'end_user' },
  ];

  const builderStatusOptions = [
    { label: 'DNQ', value: 'dnq' },
    { label: 'Won', value: 'won' },
    { label: 'Lost', value: 'lost' },
    { label: 'Unknown', value: 'unknown' },
    { label: 'Withdrew', value: 'withdrew' },
  ];

  const bmsSpecifiedOptions = [
    { label: 'Open Tender', value: 'open_tender' },
    { label: 'Other Specified', value: 'other_specified' },
    { label: 'Select Tender', value: 'select_tender' },
    { label: 'BT Specified', value: 'bt_specified' },
    { label: 'BT Incumbent', value: 'bt_incumbent' },
  ];

  const yesNoOptions = [
    { label: 'Yes', value: true },
    { label: 'No', value: false },
  ];

  const expectedOrderDateOptions = [
    { label: 'Unknown', value: 'unknown' },
    { label: '2025 Q1', value: '2025_q1' },
    { label: '2025 Q2', value: '2025_q2' },
    { label: '2025 Q3', value: '2025_q3' },
    { label: '2025 Q4', value: '2025_q4' },
    { label: '2026 Q1', value: '2026_q1' },
    { label: '2026 Q2', value: '2026_q2' },
    { label: '2026 Q3', value: '2026_q3' },
    { label: '2026 Q4', value: '2026_q4' },
    { label: '2027 Q1', value: '2027_q1' },
    { label: '2027 Q2', value: '2027_q2' },
    { label: '2027 Q3', value: '2027_q3' },
    { label: '2027 Q4', value: '2027_q4' },
    { label: '2028 Q1', value: '2028_q1' },
    { label: '2028 Q2', value: '2028_q2' },
    { label: '2028 Q3', value: '2028_q3' },
    { label: '2028 Q4', value: '2028_q4' },
    { label: 'Over 3 Years', value: 'over_3_years' },
  ];

  const includesInstallOptions = [
    { label: 'Yes', value: 'yes' },
    { label: 'No', value: 'no' },
  ];

  const mechElecOptions = [
    { label: 'W&F', value: 'w_and_f' },
    { label: 'Ralcrest', value: 'ralcrest' },
    { label: 'MES', value: 'mes' },
    { label: 'TES', value: 'tes' },
  ];

  const mechElecStatusOptions = [
    { label: 'DNQ', value: 'dnq' },
    { label: 'Unknown', value: 'unknown' },
    { label: 'Lost', value: 'lost' },
    { label: 'Won', value: 'won' },
    { label: 'Withdrew', value: 'withdrew' },
  ];

  const competitorOptions = [
    { label: 'ALC', value: 'ALC' },
    { label: 'Alerton', value: 'Alerton' },
    { label: 'Austec', value: 'Austec' },
    { label: 'Delta', value: 'Delta' },
    { label: 'EA', value: 'EA' },
    { label: 'EC Controls', value: 'EC Controls' },
    { label: 'Honeywell', value: 'Honeywell' },
    { label: 'IBS', value: 'IBS' },
    { label: 'Innotech', value: 'Innotech' },
    { label: 'IOPro', value: 'IOPro' },
    { label: 'JCI', value: 'JCI' },
    { label: 'Schneider', value: 'Schneider' },
    { label: 'Siemens', value: 'Siemens' },
    { label: 'Unknown', value: 'Unknown' },
  ];

  const contractorStatusOptions = [
    { label: 'Pending', value: 'pending' },
    { label: 'DNQ', value: 'dnq' },
    { label: 'BnB', value: 'bnb' },
    { label: 'Unknown', value: 'unknown' },
    { label: 'Lost', value: 'lost' },
    { label: 'In Negotiation', value: 'in_negotiation' },
    { label: 'Won', value: 'won' },
    { label: 'Withdrew', value: 'withdrew' },
  ];

  const filteredCompetitorOptions = ref([...competitorOptions]);

  function filterCompetitors(val, update) {
    update(() => {
      if (!val) {
        filteredCompetitorOptions.value = [...competitorOptions];
      } else {
        const needle = val.toLowerCase();
        filteredCompetitorOptions.value = competitorOptions.filter(
          (opt) => opt.label.toLowerCase().includes(needle),
        );
      }
    });
  }

  const competitorStatusOptions = [
    { label: 'DNQ', value: 'dnq' },
    { label: 'Unknown', value: 'unknown' },
    { label: 'Lost', value: 'lost' },
    { label: 'Won', value: 'won' },
    { label: 'Withdrew', value: 'withdrew' },
  ];

  // Computed properties
  const isEdit = computed(() => !!props.tender);

  const marketSectorOptions = computed(() => {
    return marketSectors.value.map((sector) => ({
      label: sector.name,
      value: sector.id,
      icon: sector.icon || 'category',
      color: sector.color || 'primary',
    }));
  });

  const userOptions = computed(() => {
    return users.value.map((user) => ({
      label: user.name,
      value: user.id,
    }));
  });

  // Methods
  async function loadMarketSectors() {
    loadingMarketSectors.value = true;
    try {
      const response = await api.get('/market-sectors');
      marketSectors.value = response || [];
      filteredMarketSectorOptions.value = marketSectorOptions.value;
    } catch (error) {
      console.error('Failed to load market sectors:', error);
    } finally {
      loadingMarketSectors.value = false;
    }
  }

  function filterMarketSectors(val, update) {
    update(() => {
      if (!val) {
        filteredMarketSectorOptions.value = marketSectorOptions.value;
      } else {
        const needle = val.toLowerCase();
        filteredMarketSectorOptions.value = marketSectorOptions.value.filter(
          (opt) => opt.label.toLowerCase().includes(needle),
        );
      }
    });
  }

  async function createMarketSector(val, done) {
    if (!val || !val.trim()) {return;}

    const name = val.trim();

    // Check if already exists
    const exists = marketSectors.value.some(
      (s) => s.name.toLowerCase() === name.toLowerCase(),
    );
    if (exists) {
      $q.notify({ type: 'warning', message: 'That sector already exists' });
      return;
    }

    try {
      const response = await api.post('/market-sectors', {
        name,
        icon: 'category',
        color: '#78909C',
      });

      const newSector = response?.data || response;
      marketSectors.value.push(newSector);
      filteredMarketSectorOptions.value = marketSectorOptions.value;

      // Select the newly created sector
      done(newSector.id, 'add-unique');

      $q.notify({ type: 'positive', message: `Market sector "${name}" created` });
    } catch (error) {
      $q.notify({ type: 'negative', message: 'Failed to create market sector' });
    }
  }

  async function loadUsers() {
    loadingUsers.value = true;
    try {
      const response = await api.get('/users/list');
      users.value = response?.data || response || [];
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      loadingUsers.value = false;
    }
  }

  function onConsultantSelected(org) {
  }

  function onBuilderSelected(org) {
  }
  function resetForm() {
    form.value = {
      name: '',
      reference_number: '',
      description: '',
      site_id: null,
      status: 'pending',
      due_date: '',
      submission_date: '',
      expected_start_date: '',
      estimated_value: null,
      project_type: '',
      contractors: [],
      win_likelihood: 50,
      priority: 'medium',
      tags: [],
      internal_notes: '',
      // New pipeline fields
      requested_date: '',
      expected_order_date: '',
      indicative_program: '',
      market_sector_id: null,
      tender_type: null,
      construction_value: null,
      default_markup: null,
      consultant_id: null,
      builder_id: null,
      builder_status: null,
      bms_specified: null,
      tridium_specified: false,
      bpic_site: false,
      eba_site: false,
      includes_install: null,
      mech_elec: null,
      mech_elec_status: null,
      competitor: null,
      competitor_status: null,
      competitor_price: null,
      estimator_id: null,
      estimator_2_id: null,
      project_manager_id: null,
    };
  }

  function populateForm(tender) {
    form.value = {
      name: tender.name || '',
      reference_number: tender.reference_number || '',
      description: tender.description || '',
      site_id: tender.site_id || null,
      status: tender.status || 'pending',
      due_date: tender.due_date || '',
      submission_date: tender.submission_date || '',
      expected_start_date: tender.expected_start_date || '',
      estimated_value: tender.estimated_value || null,
      project_type: tender.project_type || '',
      contractors: [],
      win_likelihood: tender.win_likelihood || 50,
      priority: tender.priority || 'medium',
      tags: tender.tags || [],
      internal_notes: tender.internal_notes || '',
      // New pipeline fields
      requested_date: tender.requested_date || '',
      expected_order_date: tender.expected_order_date || '',
      indicative_program: tender.indicative_program || '',
      market_sector_id: tender.market_sector_id || null,
      tender_type: tender.tender_type || null,
      construction_value: tender.construction_value || null,
      default_markup: tender.default_markup || null,
      consultant_id: tender.consultant_id || null,
      builder_id: tender.builder_id || null,
      builder_status: tender.builder_status || null,
      bms_specified: tender.bms_specified || null,
      tridium_specified: tender.tridium_specified || null,
      bpic_site: tender.bpic_site || null,
      eba_site: tender.eba_site || null,
      includes_install: tender.includes_install || null,
      mech_elec: tender.mech_elec || null,
      mech_elec_status: tender.mech_elec_status || null,
      competitor: tender.competitor || null,
      competitor_status: tender.competitor_status || null,
      competitor_price: tender.competitor_price || null,
      estimator_id: tender.estimator_id || null,
      estimator_2_id: tender.estimator_2_id || null,
      project_manager_id: tender.project_manager_id || null,
    };
  }

  async function onSubmit() {
    const isValid = await tenderForm.value.validate();
    if (!isValid) {return;}

    try {
      let result;

      // Build the tender data object with all fields
      const tenderData = {
        name: form.value.name,
        reference_number: form.value.reference_number,
        description: form.value.description,
        site_id: form.value.site_id,
        status: form.value.status,
        due_date: form.value.due_date || null,
        submission_date: form.value.submission_date || null,
        expected_start_date: form.value.expected_start_date || null,
        estimated_value: form.value.estimated_value,
        project_type: form.value.project_type,
        win_likelihood: form.value.win_likelihood,
        priority: form.value.priority,
        tags: form.value.tags,
        internal_notes: form.value.internal_notes,
        // New pipeline fields
        requested_date: form.value.requested_date || null,
        expected_order_date: form.value.expected_order_date || null,
        indicative_program: form.value.indicative_program || null,
        market_sector_id: form.value.market_sector_id || null,
        tender_type: form.value.tender_type || null,
        construction_value: form.value.construction_value || null,
        default_markup: form.value.default_markup || null,
        consultant_id: form.value.consultant_id || null,
        builder_id: form.value.builder_id || null,
        builder_status: form.value.builder_status || null,
        bms_specified: form.value.bms_specified,
        tridium_specified: form.value.tridium_specified,
        bpic_site: form.value.bpic_site,
        eba_site: form.value.eba_site,
        includes_install: form.value.includes_install || null,
        mech_elec: form.value.mech_elec || null,
        mech_elec_status: form.value.mech_elec_status || null,
        competitor: form.value.competitor || null,
        competitor_status: form.value.competitor_status || null,
        competitor_price: form.value.competitor_price || null,
        estimator_id: form.value.estimator_id || null,
        estimator_2_id: form.value.estimator_2_id || null,
        project_manager_id: form.value.project_manager_id || null,
      };

      if (isEdit.value) {
        // Update existing tender
        result = await tenderStore.updateTender(props.tender.id, tenderData);
      } else {
        // Create new tender - include contractors
        result = await tenderStore.createTender({
          ...tenderData,
          contractors: form.value.contractors.map((c) => ({
            organization_id: c.organization_id,
            organization_contact_id: c.organization_contact_id,
            is_primary: c.is_primary,
            status: c.status,
            notes: c.notes || null,
          })),
        });
      }

      emit('save', result);
    } catch (error) {
      $q.notify({
        color: 'negative',
        message: `Failed to ${isEdit.value ? 'update' : 'create'} tender: ${error.message}`,
        icon: 'warning',
      });
    }
  }

  async function saveDraft() {
    savingDraft.value = true;
    try {
      const { contractors: rawContractors, ...rest } = form.value;
      const result = await tenderStore.createTender({
        ...rest,
        status: 'draft',
        due_date: form.value.due_date || null,
        submission_date: form.value.submission_date || null,
        expected_start_date: form.value.expected_start_date || null,
        contractors: rawContractors.map((c) => ({
          organization_id: c.organization_id,
          organization_contact_id: c.organization_contact_id,
          is_primary: c.is_primary,
          status: c.status,
          notes: c.notes || null,
        })),
      });
    
      $q.notify({
        color: 'positive',
        message: 'Tender saved as draft',
        icon: 'save',
      });
    
      emit('save', result);
    } catch (error) {
      $q.notify({
        color: 'negative',
        message: `Failed to save draft: ${error.message}`,
        icon: 'warning',
      });
    } finally {
      savingDraft.value = false;
    }
  }

  function onCancel() {
    emit('cancel');
  }

  function showCreateSiteDialog() {
    showSiteDialog.value = true;
    newSiteName.value = '';
  }

  function addContractor() {
    form.value.contractors.push({
      organization_id: null,
      organization_contact_id: null,
      is_primary: false,
      status: 'pending',
      notes: '',
      _contactOptions: [],
    });
  }

  async function onContractorOrgSelected(index, org) {
    // Clear the contact when org changes
    form.value.contractors[index].organization_contact_id = null;

    if (org && org.contacts) {
      form.value.contractors[index]._contactOptions = org.contacts.map((c) => ({
        label: `${c.name} (${c.email || ''})`,
        value: c.id,
      }));
    } else if (org) {
      // Fetch contacts for this org
      try {
        const response = await organizationService.getContacts(org.id);
        const contacts = response?.data || response || [];
        form.value.contractors[index]._contactOptions = contacts.map((c) => ({
          label: `${c.name} (${c.email || ''})`,
          value: c.id,
        }));
      } catch (_error) {
        form.value.contractors[index]._contactOptions = [];
      }
    }
  }

  function onPrimaryChange(index, val) {
    if (val) {
      // Only one primary - uncheck others
      form.value.contractors.forEach((c, i) => {
        if (i !== index) {c.is_primary = false;}
      });
    }
  }

  async function createSite() {
    if (!newSiteName.value.trim()) {return;}

    try {
      const site = await api.post('/sites', { name: newSiteName.value.trim() });
      form.value.site_id = site.id;
      showSiteDialog.value = false;

      $q.notify({
        color: 'positive',
        message: 'Site created successfully',
        icon: 'check_circle',
      });
    } catch (error) {
      $q.notify({
        color: 'negative',
        message: `Failed to create site: ${error.message}`,
        icon: 'warning',
      });
    }
  }

  function removeContractor(index) {
    form.value.contractors.splice(index, 1);
  }

  function getLikelihoodColor(likelihood) {
    if (likelihood >= 75) {return 'positive';}
    if (likelihood >= 50) {return 'warning';}
    if (likelihood >= 25) {return 'orange';}
    return 'negative';
  }

  function showHelp() {
    showHelpDialog.value = true;
  }

  // Watchers
  watch(() => props.modelValue, (newVal) => {
    if (newVal) {
      // Dialog opened — load users lazily on first open
      loadUsers();
      if (props.tender) {
        populateForm(props.tender);
      } else {
        resetForm();
      }
    }
  });

  // Lifecycle hooks
  onMounted(async () => {
    // Load new data sources (users loaded lazily via watcher)
    loadMarketSectors();

    if (props.tender) {
      populateForm(props.tender);
    }
  });
</script>

<style lang="scss" scoped>
.tender-form {
  height: 100vh;
  display: flex;
  flex-direction: column;

  .form-header {
    flex-shrink: 0;
    border-radius: 0;
  }

  .form-content {
    flex: 1;
    overflow-y: auto;
    padding: 24px;

    .form-section {
      margin-bottom: 32px;
      
      .section-header {
        display: flex;
        align-items: center;
        margin-bottom: 16px;
        padding-bottom: 8px;
        border-bottom: 2px solid #f0f0f0;
        
        .section-title {
          font-size: 1.25rem;
          font-weight: 700;
          color: #333;
        }
        
        .section-subtitle {
          font-size: 0.875rem;
          color: #666;
          margin-left: auto;
        }
      }
      
      .section-content {
        .enhanced-input,
        .enhanced-select {
          :deep(.q-field__control) {
            border-radius: 8px;
          }
          
          :deep(.q-field__prepend) {
            color: #666;
          }
        }
      }
    }
  }

  .form-actions {
    flex-shrink: 0;
    padding: 16px 24px;
    border-top: 1px solid #e0e0e0;
    
    .action-btn {
      min-width: 120px;
      font-weight: 600;
      border-radius: 6px;
      padding: 8px 16px;
      
      &--primary {
        background: linear-gradient(45deg, #1976d2, #42a5f5);
        color: white;
      }
    }
  }

  .likelihood-input {
    .likelihood-preview {
      border-radius: 4px;
      overflow: hidden;
    }
  }

  .tech-checkbox {
    background: #f8f9fa;
    border-radius: 8px;
    padding: 12px 16px;
    width: 100%;

    &:hover {
      background: #eee;
    }
  }

  .advanced-options {
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    overflow: hidden;
    
    :deep(.q-expansion-item__container) {
      .q-expansion-item__content {
        background: #fafafa;
      }
    }
  }
}
</style>